import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, User } from '../api/api';

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  token: string | null;
  login: (telegram_id: string, password: string) => Promise<void>;
  adminLogin: (username: string, password: string) => Promise<void>;
  loginWithTelegram: () => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTelegram: boolean;
  loading: boolean;
  hasPhone: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isTelegram, setIsTelegram] = useState(false);

  // Проверка Telegram WebApp при монтировании
  useEffect(() => {
    const checkTelegram = () => {
      const isTg = !!(
        window.Telegram?.WebApp?.initDataUnsafe?.user ||
        window.Telegram?.WebApp?.initData ||
        window.location.search.includes('tgWebAppData')
      );
      setIsTelegram(isTg);
      console.log('Telegram WebApp detected:', isTg);
    };

    // Проверяем сразу
    checkTelegram();

    // Проверяем еще раз через 100мс (на случай если скрипт Telegram загружается)
    const timer = setTimeout(checkTelegram, 100);

    return () => clearTimeout(timer);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const loginWithTelegram = useCallback(async () => {
    try {
      const tg = window.Telegram?.WebApp;
      console.log('Telegram WebApp object:', tg);
      console.log('initDataUnsafe:', tg?.initDataUnsafe);

      const telegramUser = tg?.initDataUnsafe?.user;
      if (!telegramUser) {
        console.error('No Telegram user found in initDataUnsafe');
        setLoading(false);
        return;
      }

      console.log('Telegram user found:', telegramUser);
      tg.ready();
      tg.expand();

      console.log('Calling backend telegram-auth API...');
      const response = await authAPI.telegramAuth({
        id: telegramUser.id,
        first_name: telegramUser.first_name || '',
        last_name: telegramUser.last_name || '',
        username: telegramUser.username || '',
        photo_url: telegramUser.photo_url || '',
        auth_date: Math.floor(Date.now() / 1000),
        hash: tg.initData || 'webapp_auth',
      });

      console.log('Backend response:', response.data);

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);

      // Загружаем актуальные данные пользователя
      const userResp = await authAPI.getCurrentUser();
      const loadedUser = userResp.data;
      console.log('User loaded:', loadedUser);
      setUser(loadedUser);
      setLoading(false);

      // Авто-переход в кабинет для обычного пользователя или в админку для админа
      const currentPath = window.location.pathname;
      console.log('Current path:', currentPath);
      if (currentPath === '/' || currentPath === '/login') {
        if (loadedUser.is_admin) {
          console.log('Redirecting to /admin');
          navigate('/admin');
        } else {
          console.log('Redirecting to /dashboard');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Telegram auto-login failed:', error);
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (token) {
      loadUser();
      return;
    }

    // Если токена нет, но мы в Telegram WebApp — запускаем моментальную авторизацию
    if (isTelegram) {
      loginWithTelegram();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isTelegram]);

  const adminLogin = async (username: string, password: string) => {
    const response = await authAPI.adminLogin(username, password);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    await loadUser();
  };

  const login = async (telegram_id: string, password: string) => {
    const response = await authAPI.login(telegram_id, password);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    await loadUser();
  };

  const value = {
    user,
    setUser,
    token,
    login,
    adminLogin,
    loginWithTelegram,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isTelegram,
    loading,
    hasPhone: !!(user?.phone),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};
